import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import FolderTreeNavigation from "./FolderTreeNavigation";
import icons from "./Icons";

interface MenuItem {
  name: string;
  path?: string;
  icon: React.ReactNode;
  children?: MenuItem[];
}

interface SidebarProps {
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  isCollapsed: boolean;
  isMobile: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  isMobileMenuOpen: propIsMobileMenuOpen, 
  setIsMobileMenuOpen: propSetIsMobileMenuOpen,
  isCollapsed,
  isMobile
}) => {
  const location = useLocation();
  const { state } = useAuth();
  
  const isDocumentationRoute = location.pathname.startsWith('/documentation');

  // Usar el estado del prop si está disponible, sino usar estado local
  const isMobileMenuOpen = propIsMobileMenuOpen ?? false;
  const setIsMobileMenuOpen = propSetIsMobileMenuOpen ?? (() => {});

  useEffect(() => {
    const handleToggleSidebar = () => {
      // Dispatch event to Layout component
      const event = new CustomEvent('toggleSidebarFromSidebar');
      window.dispatchEvent(event);
    };

    const handleToggleEvent = () => {
      handleToggleSidebar();
    };

    window.addEventListener('toggleSidebar', handleToggleEvent);
    return () => window.removeEventListener('toggleSidebar', handleToggleEvent);
  }, []);

  // Definir elementos del menú
  const menuItems: MenuItem[] = [
    // Solo mostrar gestión de usuarios si el usuario es admin
    ...(state.user?.role === 'admin' ? [{
      name: 'Gestión de Usuarios',
      path: '/users',
      icon: icons.users
    }] : [])
  ];

  // Don't render sidebar in desktop when collapsed
  if (isCollapsed && !isMobile) {
    return null;
  }

  return (
    <>      
      {/* Mobile overlay */}
      {isMobile && isMobileMenuOpen && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      <div
        id="sidebar"
        className={`sidebar ${
          isMobile 
            ? `mobile ${isMobileMenuOpen ? 'open' : ''}` 
            : 'expanded'
        }`}
      >
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-header-content">
            <h1 className="sidebar-title">BBX Docs</h1>
            {!isCollapsed && (
              <div className="sidebar-footer-info">
                <p className="sidebar-footer-name">
                  {state.user?.first_name || state.user?.username}
                </p>
                <div className="sidebar-footer-status">
                  <div className="sidebar-footer-status-dot"></div>
                  <p className="sidebar-footer-status-text">En línea</p>
                </div>
              </div>
            )}
          </div>
          {/* Botón para colapsar sidebar - solo en desktop */}
          {!isMobile && (
            <button
              onClick={() => {
                // Llamar al toggle del Layout a través del contexto o prop
                const event = new CustomEvent('toggleSidebar');
                window.dispatchEvent(event);
              }}
              className="sidebar-collapse-btn"
              title={isCollapsed ? "Expandir sidebar" : "Colapsar sidebar"}
            >
              {isCollapsed ? icons.chevronRight : icons.chevronLeft}
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="sidebar-nav-container">
            {/* Folder Tree Navigation - Solo visible cuando no está colapsado */}
            {!isCollapsed && <FolderTreeNavigation />}

            {/* Separador visual */}
            {menuItems.length > 0 && !isCollapsed && (
              <div className="sidebar-nav-separator"></div>
            )}

            {/* Static Navigation Items */}
            {menuItems.length > 0 && (
              <>
                <ul className="sidebar-nav-list">
                  {menuItems.map((item) => {
                    const isActive =
                      location.pathname === item.path ||
                      (item.path && item.path !== "/documentation" && location.pathname.startsWith(`${item.path}/`));

                    return (
                      <li key={item.name}>
                        <Link
                          to={item.path || "#"}
                          onClick={() => isMobile && setIsMobileMenuOpen(false)}
                          className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                          title={isCollapsed ? item.name : undefined}
                        >
                          <span className="sidebar-nav-icon">
                            {item.icon}
                          </span>
                          {!isCollapsed && (
                            <span className="sidebar-nav-text">{item.name}</span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <div className="sidebar-footer-user">
            
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;